/**
 * One typed function per backend route (ARCH.md §4). Screens never build a URL.
 */

import type { DslSchemaResponse } from "@/lib/dsl-catalog";
import type {
  Account,
  AccountHistory,
  AccountPatchBody,
  AccountPerformance,
  AdminStats,
  AdminUserRow,
  AssistantCredential,
  AuditLogRow,
  Backtest,
  BacktestChart,
  BacktestRequest,
  BookBalance,
  BookEntriesPage,
  BookSummary,
  BuyingPower,
  ChatMessageRow,
  ChatThreadSummary,
  ConditionalOrder,
  CredentialDiagnosis,
  CredentialVerifyResult,
  Currency,
  ExchangeRate,
  HoldingsView,
  KillSwitchResult,
  MarketCalendar,
  MarketCountry,
  McpConnectRequest,
  McpConnection,
  McpConnections,
  Me,
  Notification,
  NotificationSettings,
  OnboardingGuide,
  Order,
  Orderbook,
  Position,
  PriceLimits,
  ProxyHealth,
  QuarantineReleaseResult,
  RankingDuration,
  RankingSnapshot,
  RankingType,
  RuleDsl,
  ServerPreset,
  StockChart,
  StockMasterRow,
  StockWarnings,
  Strategy,
  StrategyPreview,
  StrategyRun,
  StrategyRunDetail,
  StrategyRunOnceResult,
  SymbolQuarantineReleaseResult,
  TossCredential,
  ValidateResult,
} from "@/types/api";
import { api } from "./client";

const V1 = "/api/v1";

export interface LoginResult {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: Me;
}

export const authApi = {
  /**
   * 운영자 로그인. `totpCode` 는 서버에 TOTP 가 설정된 경우에만 필요해요 —
   * 필요 여부를 프런트가 판단하지 않고, 빠졌으면 서버가 알려줘요.
   */
  login: (body: { email: string; password: string; totpCode?: string }) =>
    api.post<LoginResult>(`${V1}/auth/login`, {
      email: body.email,
      password: body.password,
      totp_code: body.totpCode ?? null,
    }),
  me: () => api.get<Me>(`${V1}/auth/me`),
  /** 보낸 필드만 바뀌어요. 타임존은 서버가 IANA 정규 목록과 대조해 모르는 이름이면 422 예요. */
  updateMe: (body: { display_name?: string; timezone?: string }) =>
    api.patch<Me>(`${V1}/auth/me`, body),
  deleteMe: () => api.delete<void>(`${V1}/auth/me`),
};

/**
 * MCP 연결(OAuth). 동의 화면 조회는 `silent` 예요 — 만료·없음(404·410)은 화면이 직접 "AI 도구에서
 * 다시 연결해 주세요" 로 설명하고, 토스트까지 띄우면 같은 말을 두 번 해요.
 */
export const mcpApi = {
  request: (id: string) =>
    api.get<McpConnectRequest>(`${V1}/mcp/requests/${encodeURIComponent(id)}`, { silent: true }),
  approve: (id: string) =>
    api.post<{ redirect_url: string }>(
      `${V1}/mcp/requests/${encodeURIComponent(id)}/approve`,
      undefined,
      {
        silent: true,
      }
    ),
  deny: (id: string) =>
    api.post<{ redirect_url: string }>(
      `${V1}/mcp/requests/${encodeURIComponent(id)}/deny`,
      undefined,
      {
        silent: true,
      }
    ),
  connections: () => api.get<McpConnections>(`${V1}/mcp/connections`),
  disconnect: (id: string) => api.delete<McpConnection>(`${V1}/mcp/connections/${id}`),
};

export const tossApi = {
  onboarding: () => api.get<OnboardingGuide>(`${V1}/toss/onboarding`),
  credentials: () => api.get<TossCredential[]>(`${V1}/toss/credentials`),
  createCredential: (body: {
    client_id: string;
    client_secret: string;
    label?: string;
  }) =>
    api.post<{
      credential: TossCredential;
      accounts: Array<{
        id: string;
        account_seq: number;
        account_no_masked: string;
      }>;
    }>(`${V1}/toss/credentials`, body),
  verifyCredential: (id: string) =>
    api.post<CredentialVerifyResult>(`${V1}/toss/credentials/${id}/verify`),
  /** Ordered probes (token → accounts) with the failing step named. */
  diagnoseCredential: (id: string) =>
    api.get<CredentialDiagnosis>(`${V1}/toss/credentials/${id}/diagnose`),
  deleteCredential: (id: string) => api.delete<void>(`${V1}/toss/credentials/${id}`),
};

export const accountsApi = {
  list: () => api.get<Account[]>(`${V1}/accounts`),
  update: (id: string, body: AccountPatchBody) => api.patch<Account>(`${V1}/accounts/${id}`, body),
  holdings: (id: string, refresh = false) =>
    api.get<HoldingsView>(`${V1}/accounts/${id}/holdings`, {
      params: refresh ? { refresh: 1 } : undefined,
    }),
  buyingPower: (id: string, currency: "KRW" | "USD" = "KRW") =>
    api.get<BuyingPower>(`${V1}/accounts/${id}/buying-power`, {
      params: { currency },
    }),
  /**
   * 조회 기간은 **일 수**(`days`, 1~365)예요. 예전에는 `range: "3m"` 을 보냈는데 서버에
   * 그런 파라미터가 없어서 조용히 버려지고 늘 기본 30일이 돌아왔습니다.
   */
  performance: (id: string, days: number, currency: Currency = "KRW") =>
    api.get<AccountPerformance>(`${V1}/accounts/${id}/performance`, {
      params: { days, currency },
    }),
  /** 일별 계좌 시계열(자산·실현손익). `days` 는 2~365, 매일 16:00 KST 에 한 번 갱신돼요. */
  history: (id: string, days: number) =>
    api.get<AccountHistory>(`${V1}/accounts/${id}/history`, {
      params: { days },
    }),
  sync: (id: string) => api.post<void>(`${V1}/accounts/${id}/sync`),
};

/**
 * S4 kill switch — 유저 스코프 전역 라우트다(`POST /api/v1/kill-switch`, 계좌 경로 아님).
 * 서버는 오타 방지로 confirm="정지" 를 요구한다.
 */
export const killSwitchApi = {
  engage: () => api.post<KillSwitchResult>(`${V1}/kill-switch`, { confirm: "정지" }),
  release: () => api.delete<{ kill_switch: boolean; note: string }>(`${V1}/kill-switch`),
};

/** 일봉 차트 한 번에 받을 수 있는 최대 봉 수. 서버 `api/services/chart.py: MAX_BARS` 와 같은 값. */
export const MAX_CHART_BARS = 200;

export const marketApi = {
  /** USD/KRW 고시환율. 통화 합산의 유일한 출처예요. */
  exchangeRate: () => api.get<ExchangeRate>(`${V1}/market/exchange-rate`),

  /**
   * 종목 검색. 심볼 형태(국내 6자리 코드 / 미국 영문 티커)면 서버가 토스로 실시간 검증해
   * 사전에 추가하고, 아니면 **랭킹으로 누적한 사전 안에서만** 한글명·영문명으로 찾아요 —
   * 전체 종목 나열 API 가 없습니다.
   *
   * `marketCountry` 를 주면 그 시장으로 좁혀요. 규칙 편집기는 항상 자기 규칙의 시장을
   * 넘깁니다 — 다른 시장 종목을 담으면 저장이 `SYMBOL_INVALID` 로 거부되는데, 검색
   * 결과에 보여 주면 사용자는 담기를 눌러 보고 나서야 알게 돼요.
   */
  searchStocks: (q: string, limit = 20, marketCountry?: MarketCountry) =>
    api.get<StockMasterRow[]>(`${V1}/market/stocks`, {
      params: { q, limit, ...(marketCountry ? { market_country: marketCountry } : {}) },
    }),
  stock: (symbol: string) => api.get<StockMasterRow>(`${V1}/market/stocks/${symbol}`),
  /**
   * 심볼 → 종목명 사전 전체. 주문·포지션·원장 응답에는 심볼만 실려 있어서, 화면이
   * 종목코드 대신 이름을 그리려면 이게 필요해요. 서버는 종목 사전(Mongo)만 읽으니
   * 폴링 예산을 쓰지 않습니다.
   */
  stockNames: () => api.get<Record<string, string>>(`${V1}/market/stock-names`),
  /** 매수 유의사항. `buy_blocked_reason` 이 있으면 규칙이 이 종목을 사지 않아요. */
  warnings: (symbol: string) => api.get<StockWarnings>(`${V1}/market/stocks/${symbol}/warnings`),
  priceLimits: (symbol: string) =>
    api.get<PriceLimits>(`${V1}/market/price-limits`, { params: { symbol } }),
  orderbook: (symbol: string) =>
    api.get<Orderbook>(`${V1}/market/orderbook`, { params: { symbol } }),
  /** 국내 장 운영 정보. 휴장·세션 판정의 유일한 출처예요. */
  calendar: (country: "KR" | "US" = "KR") =>
    api.get<MarketCalendar>(`${V1}/market/calendar/${country}`),
  /**
   * 일봉 + 지표 오버레이. 오버레이 계산은 **서버**가 규칙 엔진과 같은 코드로 해요 —
   * 여기서 다시 구현하면 차트의 SMA 와 엔진의 SMA 가 갈라집니다.
   */
  chart: (symbol: string, params: { count?: number; overlays?: string[] } = {}) =>
    api.get<StockChart>(`${V1}/market/chart`, {
      params: {
        symbol,
        // 서버 상한(`api/services/chart.py: MAX_BARS`)을 넘으면 **422** 로 떨어져요. 여기서
        // 한 번 자르는 이유는 호출부마다 이 숫자를 기억하게 두면 그중 하나가 반드시
        // 어긋나기 때문이에요 — 실제로 매매 차트가 250을 보내 클릭마다 오류가 났어요.
        count: Math.min(params.count ?? 60, MAX_CHART_BARS),
        ...(params.overlays && params.overlays.length > 0
          ? { overlays: params.overlays.join(",") }
          : {}),
      },
    }),
  /** 랭킹. 규칙이 저장할 값과 **같은 파라미터**로 불러서 미리보기가 실제와 어긋나지 않게 해요. */
  rankings: (params: {
    type: RankingType;
    duration: RankingDuration;
    market_country?: "KR" | "US";
    count?: number;
  }) => api.get<RankingSnapshot>(`${V1}/market/rankings`, { params }),
};

export const strategiesApi = {
  list: () => api.get<Strategy[]>(`${V1}/strategies`),
  get: (id: string) => api.get<Strategy>(`${V1}/strategies/${id}`),
  create: (body: {
    name: string;
    description?: string | null;
    account_id: string;
    rule?: RuleDsl;
    preset_key?: string;
    symbol_exclusive?: boolean;
  }) => api.post<Strategy>(`${V1}/strategies`, body),
  update: (
    id: string,
    body: {
      name?: string;
      description?: string | null;
      rule?: RuleDsl;
      symbol_exclusive?: boolean;
    }
  ) => api.put<Strategy>(`${V1}/strategies/${id}`, body),
  /** 보관(soft delete) — 문서를 지우지 않고 ARCHIVED 로 내린다. `restore` 로 되돌아온다. */
  remove: (id: string) => api.delete<void>(`${V1}/strategies/${id}`),
  /** 보관 해제. 서버가 DRAFT 로 돌려주므로 켜려면 활성화 경로를 다시 타야 한다. */
  restore: (id: string) => api.post<Strategy>(`${V1}/strategies/${id}/restore`),
  duplicate: (id: string) => api.post<Strategy>(`${V1}/strategies/${id}/duplicate`),
  presets: () => api.get<ServerPreset[]>(`${V1}/strategies/presets`),
  validate: (rule: RuleDsl) => api.post<ValidateResult>(`${V1}/strategies/validate`, { rule }),
  schema: () => api.get<DslSchemaResponse>(`${V1}/strategies/dsl/schema`),
  /** 활성화 = 실거래 시작. 서버는 오타 방지로 confirm="실거래" 정확 일치를 요구한다. */
  activate: (id: string, body: { confirm: string }) =>
    api.post<Strategy>(`${V1}/strategies/${id}/activate`, body),
  pause: (id: string) => api.post<Strategy>(`${V1}/strategies/${id}/pause`),
  resume: (id: string) => api.post<Strategy>(`${V1}/strategies/${id}/resume`),
  preview: (id: string) => api.post<StrategyPreview>(`${V1}/strategies/${id}/preview`),
  runOnce: (id: string) => api.post<StrategyRunOnceResult>(`${V1}/strategies/${id}/run-once`),
  runs: (id: string) => api.get<StrategyRun[]>(`${V1}/strategies/${id}/runs`),
  run: (id: string, runId: string) =>
    api.get<StrategyRunDetail>(`${V1}/strategies/${id}/runs/${runId}`),
  positions: (id: string) => api.get<Position[]>(`${V1}/strategies/${id}/positions`),
  orders: (id: string) => api.get<Order[]>(`${V1}/strategies/${id}/orders`),
};

export const ordersApi = {
  /** 서버 필터는 account_id·symbol·limit 뿐 — 상태·전략 필터는 훅에서 클라이언트 측으로 거른다. */
  list: (params?: { account_id?: string; symbol?: string; limit?: number }) =>
    api.get<Order[]>(`${V1}/orders`, { params }),
  get: (id: string, refresh = false) =>
    api.get<Order>(`${V1}/orders/${id}`, {
      params: refresh ? { refresh: 1 } : undefined,
    }),
  cancel: (id: string) => api.post<Order>(`${V1}/orders/${id}/cancel`),
};

export const positionsApi = {
  list: () => api.get<Position[]>(`${V1}/positions`),
  close: (id: string) => api.post<void>(`${V1}/positions/${id}/close`),
  conditionalOrders: () => api.get<ConditionalOrder[]>(`${V1}/conditional-orders`),
  deleteConditionalOrder: (id: string) => api.delete<void>(`${V1}/conditional-orders/${id}`),
  /**
   * 격리 해제. 수량은 서버가 계좌 스냅샷에서 다시 읽는다 — 화면이 숫자를 보내지 않는다.
   * 같은 종목에 격리된 포지션이 여러 개면 `book-quarantine-multiple-positions` 로 422를
   * 낸다 — 그 경우 `releaseQuarantineBySymbol` 로 안내한다.
   */
  releaseQuarantine: (id: string) =>
    api.post<QuarantineReleaseResult>(`${V1}/positions/${id}/release-quarantine`),
  /** 한 종목에 격리된 포지션 전부를 한 번에 해제한다 — `opened_at` 오름차순(FIFO)으로 나눠 채운다. */
  releaseQuarantineBySymbol: (accountId: string, symbol: string) =>
    api.post<SymbolQuarantineReleaseResult>(`${V1}/positions/release-quarantine-by-symbol`, {
      account_id: accountId,
      symbol,
    }),
};

/**
 * 장부(rule book) — 계좌 안 논리적 서브계좌. `/book` 은 Mongo 만 읽는다: 현금은
 * `accountsApi.buyingPower` 가 채운 30초 캐시를 읽기만 하고 새 토스 호출을 만들지
 * 않는다. 화면은 그 캐시를 데우려고 `buyingPower` 를 먼저(또는 함께) 불러야 한다 —
 * 그러지 않으면 `cash_known=false` 로 열리는 게 기본값이다.
 */
export const bookApi = {
  /**
   * 장부 요약. **통화 하나**를 봐요 — 토스는 통화를 섞어 합산하지 않고(`Price` 스키마가
   * krw·usd 별도), 섞은 합계는 어떤 계좌 실측과도 비교할 수 없어서 대조가 성립하지 않아요.
   * 미국 규칙의 장부는 `currency: "USD"` 로 조회합니다.
   */
  get: (accountId: string, currency: Currency = "KRW") =>
    api.get<BookSummary>(`${V1}/accounts/${accountId}/book`, { params: { currency } }),
  entries: (
    accountId: string,
    params?: { strategy_id?: string; symbol?: string; limit?: number; cursor?: string }
  ) => api.get<BookEntriesPage>(`${V1}/accounts/${accountId}/book/entries`, { params }),
  /** 양수 배정(FUND) · 음수 회수(WITHDRAW). `strategy_id` 를 안 주면 계좌 스코프. */
  fund: (
    accountId: string,
    body: { amount: string; strategy_id?: string | null; currency?: Currency }
  ) => api.post<BookBalance>(`${V1}/accounts/${accountId}/book/fund`, body),
};

export const backtestsApi = {
  list: (strategyId?: string) =>
    api.get<Backtest[]>(`${V1}/backtests`, {
      params: strategyId ? { strategy_id: strategyId } : undefined,
    }),
  get: (id: string) => api.get<Backtest>(`${V1}/backtests/${id}`),
  chart: (id: string, symbol: string) =>
    api.get<BacktestChart>(`${V1}/backtests/${id}/chart`, { params: { symbol } }),
  create: (body: BacktestRequest) => api.post<Backtest>(`${V1}/backtests`, body),
  remove: (id: string) => api.delete<void>(`${V1}/backtests/${id}`),
};

export const notificationsApi = {
  list: () => api.get<Notification[]>(`${V1}/notifications`),
  read: (id: string) => api.post<void>(`${V1}/notifications/${id}/read`),
  readAll: () => api.post<void>(`${V1}/notifications/read-all`),
  settings: () => api.get<NotificationSettings>(`${V1}/notifications/settings`),
  updateSettings: (body: NotificationSettings) =>
    api.put<NotificationSettings>(`${V1}/notifications/settings`, body),
};

export const adminApi = {
  proxyHealth: () => api.get<ProxyHealth>(`${V1}/admin/proxy/health`),
  stats: () => api.get<AdminStats>(`${V1}/admin/stats`),
  users: () => api.get<AdminUserRow[]>(`${V1}/admin/users`),
  auditLogs: () => api.get<AuditLogRow[]>(`${V1}/admin/audit-logs`),
  killSwitch: (body: {
    scope: "global" | "user" | "strategy";
    target_id?: string;
  }) => api.post<KillSwitchResult>(`${V1}/admin/kill-switch`, body),
};

/**
 * 대화형 어시스턴트 — 자격증명 · 스레드. 메시지 스트림(`POST .../messages`)은 SSE 라
 * `apiClient` 를 안 쓰고 `fetch` 로 직접 부른다(`lib/assistant-stream.ts`) — 여기 넣지 않는다.
 */
export const assistantApi = {
  /** 등록된 키가 없으면 `null`. 응답에는 원문이 절대 실리지 않는다(`key_masked` 뿐). */
  getCredential: () => api.get<AssistantCredential | null>(`${V1}/assistant/credential`),
  /** 등록·교체 겸용 — 이미 있으면 덮어쓴다. 원문은 이 요청의 body 에서만 존재한다. */
  putCredential: (body: { api_key: string; model?: string; base_url?: string | null }) =>
    api.put<AssistantCredential>(`${V1}/assistant/credential`, body),
  deleteCredential: () => api.delete<{ status: string }>(`${V1}/assistant/credential`),

  listThreads: () => api.get<ChatThreadSummary[]>(`${V1}/assistant/threads`),
  createThread: () => api.post<ChatThreadSummary>(`${V1}/assistant/threads`, {}),
  getThread: (id: string) =>
    api.get<ChatThreadSummary & { messages: ChatMessageRow[] }>(`${V1}/assistant/threads/${id}`),
  /** 제목만 바꾼다 — 자동 제목은 첫 메시지 앞 40자라 문장 중간에서 잘린다. */
  renameThread: (id: string, title: string) =>
    api.patch<ChatThreadSummary>(`${V1}/assistant/threads/${id}`, { title }),
  deleteThread: (id: string) => api.delete<{ status: string }>(`${V1}/assistant/threads/${id}`),
  confirmTool: (threadId: string, body: { message_id: string; approved: boolean }) =>
    api.post<{ confirm_state: string }>(`${V1}/assistant/threads/${threadId}/confirm`, body),
};
